<template>
	<div class="menu-item" :id="id">
		<div class="menu-item__title">
			<div class="menu-item__title-text" v-if="title">
				{{ title }}
				<span class="menu-item__title-tag" v-if="tag">{{ tag }}</span>
			</div>
			<DefaultButton
				v-if="modalButtonLabel && modal"
				class="menu-item__modal-button"
				:cta-link="{ text: modalButtonLabel }"
				color="ghost-light"
				:gtm="modalGtm"
				:modal="modal"
			>
				{{ modalButtonLabel }}
			</DefaultButton>
		</div>
		<div
			class="menu-item__description"
			v-if="description"
			v-html="description"
		></div>
		<div v-if="images" class="menu-item__images">
			<ZoomImage
				v-if="isZoomImage"
				:image="images.image"
				:image-high-res="images.imageHighRes"
				:fields="{}"
				class="menu-item__img"
			/>
			<template v-else-if="images.length > 0">
				<PictureImg
					v-for="image in images"
					:key="image?.src"
					:media="image"
					class="menu-item__img"
				/>
			</template>
		</div>
		<div
			class="menu-item__sub-description"
			v-if="subdescription"
			v-html="subdescription"
		></div>
		<DefaultButton
			class="menu-item__button"
			v-if="(ctaLink && ctaLink.url) || ctaModal"
			:cta-link="ctaLink"
			:icon="ctaIcon"
			:color="ctaColor"
			:gtm="ctaGtm"
			:modal="ctaModal"
			:disabled="ctaDisabled"
			:aria-label="ctaAriaLabel"
		>
			{{ ctaLink.text }}
		</DefaultButton>
		<p class="menu-item__tooltip" v-if="tooltip">{{ tooltip }}</p>
	</div>
</template>

<script setup>
const props = defineProps({
	fields: { type: Object, required: true },
	rendering: { type: Object, required: false },
});

const title = computed(() => props.fields?.title?.value);
const description = computed(() => props.fields?.description?.value);
const subdescription = computed(() => props.fields?.subdescription?.value);
const modalButtonLabel = computed(() => props.fields?.modalButtonLabel?.value);
const tag = computed(() => props.fields?.tag?.value);
const modalGtm = computed(() => props.fields?.modalGtm?.value);
const modal = computed(() => props.fields?.modal);
const tooltip = computed(() => props.fields?.tooltip?.value);
const ctaLink = computed(() => props.fields?.ctaLink?.value);
const ctaDisabled = computed(() => props.fields?.ctaDisabled?.value);
const ctaAriaLabel = computed(() => props.fields?.ctaAriaLabel?.value);
const ctaIcon = computed(() => props.fields?.ctaIcon?.value);
const ctaColor = computed(() => props.fields?.ctaColor?.value);
const ctaGtm = computed(() => props.fields?.ctaGtm?.value);
const ctaModal = computed(() => props.fields?.ctaModal?.value);
const id = computed(() => props.fields?.id?.value);
const isZoomImage = computed(() => props.fields?.isZoomImage?.value);
const images = computed(() => {
	if (isZoomImage.value) {
		return {
			image: getImageObj(props.fields?.images?.[0]),
			imageHighRes: getImageObj(props.fields?.images?.[1]),
		};
	} else {
		return props.fields?.images?.slice(0, 2).map((image) => {
			return getImageObj(image);
		});
	}
});

function getImageObj(image) {
	if (!image) return null;
	return {
		src: image.url,
		alt: image.fields?.Alt?.value,
		width: image.fields?.Width?.value,
		height: image.fields?.Height?.value,
	};
}
</script>

<style lang="scss">
.menu-item {
	scroll-margin-top: calc(var(--header-height, 54px) + 16px);
	margin: var(--spacing-5, 40px) auto;

	.menu-item__title {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: var(--spacing-2, 16px);
	}

	.menu-item__title-text {
		color: var(--color-white, #fff);
		font-family: var(--primary-font, sans-serif);
		font-size: var(--text-lg, 20px);
		font-weight: 300;
		line-height: 140%;
		display: flex;
		align-items: center;
		gap: var(--spacing-1, 8px);
	}

	.menu-item__title-tag {
		color: var(--Primary-Night-Sky, #191a1a);
		font-family: var(--primary-font, sans-serif);
		font-size: 11px;
		font-weight: 400;
		line-height: 1;
		text-transform: uppercase;
		display: inline-flex;
		padding: var(--spacing-05, 4px) var(--spacing-1, 8px);
		justify-content: center;
		align-items: center;
		border-radius: 2px;
		background: #2eac83;
	}

	.menu-item__modal-button {
		padding: var(--spacing-1, 8px) var(--spacing-3, 24px);
		border-radius: 48px;
		min-width: fit-content;
	}

	.menu-item__description,
	.menu-item__sub-description {
		margin: var(--spacing-2, 16px) auto;
		color: var(--color-grey-3, #989898);
		font-family: var(--primary-font, sans-serif);
		font-size: var(--text-base, 16px);
		font-weight: 300;
		line-height: 140%;
	}

	.menu-item__sub-description {
		font-weight: 400;
	}

	.menu-item__button {
		margin: var(--spacing-4, 32px) auto var(--spacing-2, 16px) auto;
		width: 100%;
		color: var(--color-white, #fff);
	}

	.menu-item__tooltip {
		color: var(--color-grey-3, #989898);
		text-align: center;
		width: 100%;
		font-family: var(--primary-font, sans-serif);
		font-size: var(--text-xs, 12px);
		font-weight: 300;
		line-height: 128%;
		margin: var(--spacing-2, 16px) auto;
	}

	.menu-item__images {
		display: flex;
		justify-content: center;
		align-items: center;
		flex-direction: row;
		gap: var(--spacing-3, 24px);
		margin-top: var(--spacing-2, 16px);
	}

	.zoom-img {
		width: 100%;
		max-width: 100%;
		padding: 0;
	}

	.picture-img-wrapper {
		width: 100%;
	}

	.picture-img img {
		padding: 0;
		margin-bottom: 0;
		border-radius: 16px;
		overflow: hidden;
		height: 100%;
	}
}
</style>
